import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Modal, message, Spin, Empty } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import CreditCardLogo from "../../../../client/settings/components/CreditCardLogo";
import ClientPaymentModal from "./ClientPaymentModal";
import clientApi from "../../store/api";

const PaymentMethodsTab = ({ client }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchPaymentMethods = useCallback(async () => {
    if (!client?.id) return;

    setLoading(true);
    try {
      const methods = await clientApi.getClientPaymentMethods(client.id);
      setPaymentMethods(methods || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      messageApi.error("Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  }, [client?.id, messageApi]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleDeletePaymentMethod = useCallback(
    async (methodId) => {
      if (!client?.id) return;

      setLoading(true);
      try {
        await clientApi.deleteClientPaymentMethod(client.id, methodId);
        messageApi.success("Payment method deleted successfully");
        fetchPaymentMethods();
      } catch (error) {
        console.error("Error deleting payment method:", error);
        messageApi.error("Failed to delete payment method");
      } finally {
        setLoading(false);
        setDeleteModalVisible(false);
        setMethodToDelete(null);
      }
    },
    [client?.id, fetchPaymentMethods, messageApi]
  );

  const handleSetDefault = useCallback(
    async (methodId) => {
      if (!client?.id) return;

      setLoading(true);
      try {
        await clientApi.setClientDefaultPaymentMethod(client.id, methodId);
        messageApi.success("Default payment method updated");
        fetchPaymentMethods();
      } catch (error) {
        console.error("Error setting default payment method:", error);
        messageApi.error("Failed to update default payment method");
      } finally {
        setLoading(false);
      }
    },
    [client?.id, fetchPaymentMethods, messageApi]
  );

  const handleManageInStripe = useCallback(async () => {
    if (!client?.id) return;

    const confirmed = window.confirm(
      "You will redirect this client to Stripe to manage their payment methods.\n\nPlease note: It may take up to 2 minutes for Stripe to show all subscriptions and payment methods.\n\nDo you want to continue?"
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await clientApi.createClientPortalSession(client.id);
      window.open(response.url, "_blank");
    } catch (error) {
      console.error("Error creating Stripe portal session:", error);
      messageApi.error("Failed to open Stripe portal");
    } finally {
      setLoading(false);
    }
  }, [client?.id, messageApi]);

  const handleAddPaymentMethod = useCallback(() => {
    if (!client?.id) return;
    setOpenPaymentMethodModal(true);
  }, [client?.id]);

  const confirmDelete = (method) => {
    setMethodToDelete(method);
    setDeleteModalVisible(true);
  };

  const renderPaymentMethod = (pm, index) => {
    if (!pm || !pm.id) return null;

    const isCard = pm.type === "card";
    const isBankAccount = pm.type === "us_bank_account";
    const isCashApp = pm.type === "cashapp";
    const isDefault = pm.is_default;

    return (
      <div style={style.cardContainer} key={pm.id}>
        <Card
          title=""
          style={{
            ...style.pmCardStyle,
            ...(isDefault ? style.defaultCardStyle : {})
          }}
          hoverable
          actions={[
            isDefault ? (
              <span key="default" style={{ color: "#1890ff", fontWeight: "bold" }}>
                Default
              </span>
            ) : (
              <Button
                key="default"
                type="link"
                onClick={() => handleSetDefault(pm.id)}
                size="small"
              >
                Set as Default
              </Button>
            ),
            <Button
              key="delete"
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(pm)}
              size="small"
            >
              Delete
            </Button>
          ]}
        >
          <div style={style.cardContent}>
            {isCard && (
              <>
                <CreditCardLogo brand={pm.brand} type={pm.type} />
                <div>
                  <div style={{ display: "flex" }}>
                    <b>{pm.name}</b>
                    <h4 style={{ color: "#4d4d4d", marginLeft: "8px" }}>{pm.brand}</h4>
                  </div>
                  <div>**** **** **** {pm.last4}</div>
                  <div>
                    Exp {pm.exp_month}/{pm.exp_year}
                  </div>
                </div>
              </>
            )}
            {isBankAccount && (
              <>
                <CreditCardLogo brand={pm.brand} type={pm.type} />
                <div>
                  <div style={{ display: "flex" }}>
                    <b>{pm.name}</b>
                    <h4 style={{ color: "#4d4d4d", marginLeft: "8px" }}>{pm.brand}</h4>
                  </div>
                  <div>{pm.account_type} Account</div>
                  <div>****{pm.last4}</div>
                  {pm.status === "verification_required" && (
                    <div>
                      <div
                        style={{ color: "#f5a623", fontSize: "12px", marginTop: "5px" }}
                      >
                        ⚠️ Verification Required
                      </div>
                      <Button
                        type="link"
                        size="small"
                        onClick={handleManageInStripe}
                        style={{ padding: "0", color: "#f5a623", fontSize: "11px" }}
                      >
                        Verify in Stripe →
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
            {isCashApp && (
              <>
                <CreditCardLogo brand={pm.brand} type={pm.type} />
                <div>
                  <div style={{ display: "flex" }}>
                    <b>{pm.name}</b>
                    <h4 style={{ color: "#4d4d4d", marginLeft: "8px" }}>{pm.brand}</h4>
                  </div>
                  <div>Cash App Pay</div>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  };

  if (!client?.id) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Empty description="No client selected" />
      </div>
    );
  }

  return (
    <div className="payment-methods-tab">
      {contextHolder}
      <div
        className="mb-3"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px"
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Payment Methods for {client.name}</h3>
          <p style={{ color: "#666", margin: 0, marginTop: "8px" }}>
            Manage payment methods on behalf of this client. Changes will affect their
            billing.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button type="primary" onClick={handleAddPaymentMethod}>
            Add Payment Method
          </Button>
          <Button type="default" onClick={handleManageInStripe}>
            Manage in Stripe ↗
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        {!paymentMethods?.length && !loading && (
          <Card>
            <Empty
              description="This client has no payment methods configured"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        )}
        {paymentMethods?.map(renderPaymentMethod)}
      </Spin>

      <ClientPaymentModal
        open={openPaymentMethodModal}
        onClose={() => setOpenPaymentMethodModal(false)}
        clientId={client?.id}
        onSuccess={fetchPaymentMethods}
      />

      <Modal
        title="Delete Payment Method"
        visible={deleteModalVisible}
        onOk={() => handleDeletePaymentMethod(methodToDelete?.id)}
        onCancel={() => {
          setDeleteModalVisible(false);
          setMethodToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okType="danger"
      >
        <p>
          Are you sure you want to delete this payment method for{" "}
          <strong>{client?.name}</strong>?
        </p>
        {methodToDelete && (
          <p>
            <strong>{methodToDelete.name}</strong> -{" "}
            {methodToDelete.type === "card"
              ? `****${methodToDelete.last4}`
              : `${methodToDelete.account_type} ****${methodToDelete.last4}`}
          </p>
        )}
      </Modal>
    </div>
  );
};

const style = {
  pmCardStyle: {
    width: "300px",
    margin: "10px"
  },
  defaultCardStyle: {
    backgroundColor: "rgba(24, 144, 255, 0.1)",
    border: "1px solid rgba(24, 144, 255, 0.3)"
  },
  cardContainer: {
    padding: "10px",
    display: "inline-block"
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  }
};

export default PaymentMethodsTab;
